import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { createId } from "@/lib/ids";
import { pointInPolygon } from "./pathGeometry";
import { planarizeVectorNetwork } from "./vectorPlanarization";
import { vectorRegionGeometries, type VectorRegionGeometry } from "./vectorRegions";

type Point = { x: number; y: number };

export interface ShapeBuilderRegion {
  id: string;
  contour: Point[];
}

export interface ShapeBuilderSelection {
  addedRegionIds: string[];
  removedRegionIds: string[];
}

export function shapeBuilderRegions(network: VectorNetwork): ShapeBuilderRegion[] {
  return vectorRegionGeometries(planarizeVectorNetwork(network)).map(regionFromGeometry);
}

export function hitTestShapeBuilderRegion(network: VectorNetwork, point: Point): ShapeBuilderRegion | undefined {
  return [...shapeBuilderRegions(network)]
    .reverse()
    .find(region => pointInPolygon(point, region.contour));
}

export function materializeShapeBuilderNetwork(
  network: VectorNetwork,
  selection: ShapeBuilderSelection,
): VectorNetwork | undefined {
  const planarized = planarizeVectorNetwork(network);
  const regions = vectorRegionGeometries(planarized);
  const added = new Set(selection.addedRegionIds);
  const removed = new Set(selection.removedRegionIds);
  const activeRegions = regions.filter(region => {
    const selected = added.size > 0 ? added.has(region.region.id) : true;
    return selected && !removed.has(region.region.id);
  });

  if (activeRegions.length === 0) {
    return undefined;
  }

  return regionsToVectorNetwork(planarized, activeRegions);
}

function regionFromGeometry(geometry: VectorRegionGeometry): ShapeBuilderRegion {
  return {
    id: geometry.region.id,
    contour: geometry.contour.map(point => ({ x: point.x, y: point.y })),
  };
}

function regionsToVectorNetwork(network: VectorNetwork, regions: VectorRegionGeometry[]): VectorNetwork | undefined {
  const selectedHalfEdges = new Set(regions.flatMap(region => region.edgeRefs.map(halfEdgeKey)));
  const boundaryRefs = regions
    .flatMap(region => region.edgeRefs)
    .filter(ref => !selectedHalfEdges.has(halfEdgeKey({ edgeId: ref.edgeId, forward: !ref.forward })));
  if (boundaryRefs.length === 0) {
    return undefined;
  }

  const sourceVerticesById = new Map(network.vertices.map(vertex => [vertex.id, vertex]));
  const sourceEdgesById = new Map(network.edges.map(edge => [edge.id, edge]));
  const vertexIds = new Map<string, string>();
  const vertices: VectorVertex[] = [];
  const edges: VectorEdge[] = boundaryRefs.flatMap(ref => {
    const edge = sourceEdgesById.get(ref.edgeId);
    if (!edge) {
      return [];
    }

    const fromVertexId = remappedVertexId(ref.forward ? edge.from.vertexId : edge.to.vertexId, sourceVerticesById, vertexIds, vertices);
    const toVertexId = remappedVertexId(ref.forward ? edge.to.vertexId : edge.from.vertexId, sourceVerticesById, vertexIds, vertices);
    if (!fromVertexId || !toVertexId) {
      return [];
    }

    if (ref.forward) {
      return [{
        id: createId("edge"),
        from: cloneEndpoint(edge.from, fromVertexId),
        to: cloneEndpoint(edge.to, toVertexId),
        directed: edge.directed,
      }];
    }

    return [{
      id: createId("edge"),
      from: cloneEndpoint(edge.to, fromVertexId),
      to: cloneEndpoint(edge.from, toVertexId),
      directed: edge.directed,
    }];
  });

  if (vertices.length === 0 || edges.length === 0) {
    return undefined;
  }

  return {
    vertices,
    edges,
    regions: [],
    fillRule: "evenodd",
  };
}

function remappedVertexId(
  sourceVertexId: string,
  sourceVerticesById: Map<string, VectorVertex>,
  vertexIds: Map<string, string>,
  vertices: VectorVertex[],
): string | undefined {
  const existing = vertexIds.get(sourceVertexId);
  if (existing) {
    return existing;
  }

  const source = sourceVerticesById.get(sourceVertexId);
  if (!source) {
    return undefined;
  }

  const nextId = createId("vertex");
  vertexIds.set(sourceVertexId, nextId);
  vertices.push({
    ...source,
    id: nextId,
  });
  return nextId;
}

function cloneEndpoint(endpoint: VectorEdge["from"], vertexId: string): VectorEdge["from"] {
  return {
    vertexId,
    handle: endpoint.handle ? { ...endpoint.handle } : undefined,
  };
}

function halfEdgeKey(ref: { edgeId: string; forward: boolean }): string {
  return `${ref.edgeId}:${ref.forward ? "f" : "r"}`;
}
